import { snapToPixel } from '../util/math.js';
import { fieldMetrics } from '../field/metrics.js';
import { puntCarryBallPoint, puntSnapBallPoint } from '../player/model.js';
import type { ArcadeReplayFrameSnapshot, ArcadeReplayPackage } from '../../arcade/contracts.js';
import {
  applyArcadeReplayFrameToPlayers,
  blendArcadeReplayPlayersFromShell,
  defaultArcadeOwnerBallPointForReplayFrame,
  mapArcadeFieldXToWorldX,
  mapArcadeFieldYToWorldY,
} from './arcade-render.js';

type ReplayBallPoint = {
  ballX: number;
  ballY: number;
  arcadeHeight?: number;
  arcadeGroundY?: number;
  arcadeState: ArcadeReplayFrameSnapshot['ball']['state'];
  arcadeOwnerRole: ArcadeReplayFrameSnapshot['ball']['ownerRendererRole'];
};

const ACTIVE_PUNT_MOTION_STATES = new Set([
  'receiving',
  'punt-prep',
  'release',
  'protection',
  'rush',
  'set-return',
  'coverage',
  'fielding',
  'return',
  'being-tackled',
]);

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function stabilizePuntReplayFrames(frames: ArcadeReplayFrameSnapshot[]): ArcadeReplayFrameSnapshot[] {
  const lastPlayerPositionByRole = new Map<string, { x: number; y: number }>();
  let lastBallPoint = frames[0]
    ? {
        x: Number.isFinite(frames[0].ball?.x) ? frames[0].ball.x : 0,
        y: Number.isFinite(frames[0].ball?.y) ? frames[0].ball.y : 26.665,
        z: Number.isFinite(frames[0].ball?.z) ? frames[0].ball.z : 0,
      }
    : { x: 0, y: 26.665, z: 0 };

  return frames.map((frame) => {
    const players = frame.players.map((player) => {
      const previous = player.rendererRole ? lastPlayerPositionByRole.get(player.rendererRole) : null;
      const nextX = Number.isFinite(player.x) ? player.x : previous?.x ?? lastBallPoint.x;
      const nextY = Number.isFinite(player.y) ? player.y : previous?.y ?? lastBallPoint.y;
      const sanitizedPlayer = (nextX === player.x && nextY === player.y)
        ? player
        : {
            ...player,
            x: nextX,
            y: nextY,
            vx: Number.isFinite(player.vx) ? player.vx : 0,
            vy: Number.isFinite(player.vy) ? player.vy : 0,
          };
      if (player.rendererRole) {
        lastPlayerPositionByRole.set(player.rendererRole, { x: nextX, y: nextY });
      }
      return sanitizedPlayer;
    });

    const owner = frame.ball.ownerRendererRole
      ? players.find((player) => player.rendererRole === frame.ball.ownerRendererRole)
      : null;
    const nextBallX = Number.isFinite(frame.ball.x) ? frame.ball.x : owner?.x ?? lastBallPoint.x;
    const nextBallY = Number.isFinite(frame.ball.y) ? frame.ball.y : owner?.y ?? lastBallPoint.y;
    const nextBallZ = Number.isFinite(frame.ball.z) ? frame.ball.z : lastBallPoint.z;
    lastBallPoint = { x: nextBallX, y: nextBallY, z: nextBallZ };

    if (nextBallX === frame.ball.x && nextBallY === frame.ball.y && nextBallZ === frame.ball.z && players.every((player, index) => player === frame.players[index])) {
      return frame;
    }

    return {
      ...frame,
      players,
      ball: {
        ...frame.ball,
        x: nextBallX,
        y: nextBallY,
        z: nextBallZ,
      },
    };
  });
}

function blendBallPoint(from: ReplayBallPoint, to: ReplayBallPoint, amount: number): ReplayBallPoint {
  const clamped = Math.max(0, Math.min(1, amount));
  return {
    ballX: snapToPixel(from.ballX + (to.ballX - from.ballX) * clamped),
    ballY: snapToPixel(from.ballY + (to.ballY - from.ballY) * clamped),
    arcadeHeight: (from.arcadeHeight ?? 0) + ((to.arcadeHeight ?? 0) - (from.arcadeHeight ?? 0)) * clamped,
    arcadeGroundY: snapToPixel(
      (from.arcadeGroundY ?? from.ballY + 4)
        + ((to.arcadeGroundY ?? to.ballY + 4) - (from.arcadeGroundY ?? from.ballY + 4)) * clamped,
    ),
    arcadeState: to.arcadeState ?? from.arcadeState,
    arcadeOwnerRole: to.arcadeOwnerRole ?? from.arcadeOwnerRole,
  };
}

function rawBallPointForReplayFrame(replayFrame: ArcadeReplayFrameSnapshot, direction: number, metrics: ReturnType<typeof fieldMetrics>) {
  const groundBallY = mapArcadeFieldYToWorldY(replayFrame.ball.y, metrics) + 8;
  const flightLift = replayFrame.ball.state === 'punt-flight'
    ? replayFrame.ball.z * 19
    : replayFrame.ball.state === 'punt-bounce'
      ? replayFrame.ball.z * 8
      : replayFrame.ball.z * 4;
  return {
    ballX: mapArcadeFieldXToWorldX(replayFrame.ball.x, direction),
    ballY: snapToPixel(groundBallY - flightLift),
    arcadeHeight: Math.max(0, replayFrame.ball.z),
    arcadeGroundY: snapToPixel(groundBallY + 4),
    arcadeState: replayFrame.ball.state,
    arcadeOwnerRole: replayFrame.ball.ownerRendererRole,
  };
}

function ownedBallPointForReplayFrame(
  replayFrame: ArcadeReplayFrameSnapshot,
  direction: number,
  metrics: ReturnType<typeof fieldMetrics>,
): ReplayBallPoint | null {
  return defaultArcadeOwnerBallPointForReplayFrame(replayFrame, direction, metrics, [], { handYOffset: 6 });
}

function ballPointForReplayFrame(
  replayFrame: ArcadeReplayFrameSnapshot,
  mappedOffense: any[],
  direction: number,
  metrics: ReturnType<typeof fieldMetrics>,
  losX: number,
  timing: { snapStartFrame: number; snapEndFrame: number; puntPrepStartFrame: number; puntPrepBlendEndFrame: number; },
): ReplayBallPoint {
  const rawBallPoint = rawBallPointForReplayFrame(replayFrame, direction, metrics);
  const ownedBall = ownedBallPointForReplayFrame(replayFrame, direction, metrics);
  if (ownedBall) {
    return ownedBall;
  }

  const snapper = [...mappedOffense]
    .filter((player) => player.role?.startsWith('PROTECT'))
    .sort((left, right) =>
      Math.abs(left.y - metrics.centerY) - Math.abs(right.y - metrics.centerY)
      || Math.abs(left.x - losX) - Math.abs(right.x - losX)
    )[0];
  const punter = mappedOffense.find((player) => player.role === 'PUNTER');

  if (replayFrame.ball.state === 'snap' && snapper) {
    const snapAnchor = {
      ...puntSnapBallPoint(snapper as any, direction, losX, snapper.y + 8),
      arcadeState: replayFrame.ball.state,
      arcadeOwnerRole: replayFrame.ball.ownerRendererRole,
    };
    const snapProgress = timing.snapEndFrame <= timing.snapStartFrame
      ? 1
      : (replayFrame.simFrame - timing.snapStartFrame) / Math.max(1, timing.snapEndFrame - timing.snapStartFrame);
    return blendBallPoint(snapAnchor, rawBallPoint, snapProgress);
  }

  if (replayFrame.ball.state === 'punt-prep' && punter) {
    const punterCarry = puntCarryBallPoint(punter as any, direction);
    const carryPoint: ReplayBallPoint = {
      ballX: punterCarry.ballX,
      ballY: punterCarry.ballY,
      arcadeState: replayFrame.ball.state,
      arcadeOwnerRole: 'PUNTER',
    };
    if (replayFrame.simFrame <= timing.puntPrepBlendEndFrame) {
      const prepProgress = timing.puntPrepBlendEndFrame <= timing.puntPrepStartFrame
        ? 1
        : (replayFrame.simFrame - timing.puntPrepStartFrame) / Math.max(1, timing.puntPrepBlendEndFrame - timing.puntPrepStartFrame);
      return blendBallPoint(rawBallPoint, carryPoint, prepProgress);
    }
    return carryPoint;
  }

  return rawBallPoint;
}

export function buildArcadePuntLiveSequence(context: any): any[] {
  const replay = context.play?.arcadeReplay as ArcadeReplayPackage | undefined;
  if (!replay?.frames?.length) {
    return [];
  }

  const replayFrames = stabilizePuntReplayFrames(replay.frames);
  const metrics = fieldMetrics();
  const firstSnapFrame = replayFrames.find((frame) => frame.ball.state === 'snap')?.simFrame ?? 0;
  const snapEndFrame = replayFrames.reduce((last, frame) => frame.ball.state === 'snap' ? frame.simFrame : last, firstSnapFrame);
  const puntPrepStartFrame = replayFrames.find((frame) => frame.ball.state === 'punt-prep')?.simFrame ?? (snapEndFrame + 1);
  const puntPrepBlendEndFrame = replayFrames.reduce((last, frame) => {
    if (frame.ball.state !== 'punt-prep') {
      return last;
    }
    if (frame.simFrame - puntPrepStartFrame > 6) {
      return last;
    }
    return frame.simFrame;
  }, puntPrepStartFrame);
  return replayFrames.map((replayFrame) => {
    const offensePlayers = blendArcadeReplayPlayersFromShell(
      context.formation.offense,
      applyArcadeReplayFrameToPlayers(context.formation.offense, replayFrame, context.direction, metrics, {
        movingThreshold: 0.02,
      }),
      replayFrame,
      { blendFrames: 5, revealMotionAt: 0.1 },
    ).map((player) => ({
      ...player,
      moving: player.moving || (ACTIVE_PUNT_MOTION_STATES.has(player.arcadeState) && !player.down && !player.tackled),
    }));
    const defensePlayers = blendArcadeReplayPlayersFromShell(
      context.formation.defense,
      applyArcadeReplayFrameToPlayers(context.formation.defense, replayFrame, context.direction, metrics, {
        movingThreshold: 0.02,
      }),
      replayFrame,
      { blendFrames: 9, revealMotionAt: 0.1 },
    ).map((player) => ({
      ...player,
      moving: player.moving || (ACTIVE_PUNT_MOTION_STATES.has(player.arcadeState) && !player.down && !player.tackled),
    }));
    return {
      offensePlayers,
      defensePlayers,
      ball: ballPointForReplayFrame(
        replayFrame,
        offensePlayers,
        context.direction,
        metrics,
        context.losX,
        {
          snapStartFrame: firstSnapFrame,
          snapEndFrame,
          puntPrepStartFrame,
          puntPrepBlendEndFrame,
        },
      ),
    };
  });
}
